import { useEffect, useState } from 'react';
import { useQuery } from 'react-query';

import { checkoutsApi } from '../../../../api/checkouts';
import { isPreviewMode } from '../PageBlocks/use-preview-bridge';
import { FONTS } from '../PageBlocks/appearance';

/** Teto do skeleton: rede travada não pode deixar a tela em branco. */
const MAX_SKELETON_MS = 8000;
/** Teto da espera pela fonte personalizada (depois disso entra a do sistema). */
const MAX_FONT_WAIT_MS = 1500;

const useTimedOut = (ms: number, active = true) => {
	const [timedOut, setTimedOut] = useState(false);
	useEffect(() => {
		if (!active) return;
		const timer = window.setTimeout(() => setTimedOut(true), ms);
		return () => window.clearTimeout(timer);
	}, [ms, active]);
	return timedOut;
};

/**
 * A página do builder (tema, aparência, fonte) já respondeu? Enquanto não,
 * a tela fica no skeleton — renderizar antes mostra o layout PADRÃO e troca
 * pelo do admin um instante depois.
 *
 * Mesma chave/opções do `useCheckoutPage`: não há fetch extra. Erro conta
 * como respondido (vale o padrão). Fonte personalizada: espera ela carregar,
 * com teto, para o texto não trocar de fonte na frente do comprador.
 */
export const useCheckoutPageSettled = (
	checkoutId?: number,
	contentUuid?: string
): boolean => {
	const page = useQuery(
		// Mesma chave do `useCheckoutPage` — inclusive o `contentUuid`.
		[checkoutsApi.findPage.key, { checkoutId, contentUuid }],
		checkoutsApi.findPage.fetch,
		{
			enabled: !!checkoutId,
			retry: 1,
			staleTime: 60_000,
			onError: () => undefined
		}
	);
	const responded = page.isFetched || page.isError;
	const font = page.data?.settings?.appearance?.font;
	const family = font && font !== 'default' ? FONTS[font]?.family : null;

	// O tema/aparência entram no documento num useEffect do useCheckoutPage,
	// que roda DEPOIS da pintura. Liberar no mesmo render pintaria um quadro
	// no padrão; este estado só vira no efeito seguinte, com o tema já aplicado.
	const [themeApplied, setThemeApplied] = useState(false);
	useEffect(() => {
		if (responded) setThemeApplied(true);
	}, [responded]);

	const [fontReady, setFontReady] = useState(false);
	useEffect(() => {
		if (!responded || !family) return;
		let cancelled = false;
		const done = () => !cancelled && setFontReady(true);
		const timer = window.setTimeout(done, MAX_FONT_WAIT_MS);
		// O <link> do Google Fonts é inserido pelo useCheckoutAppearance; o
		// load abaixo espera a fonte de fato baixar (ou falhar).
		document.fonts?.load(`16px ${family}`).then(done, done);
		return () => {
			cancelled = true;
			window.clearTimeout(timer);
		};
	}, [responded, family]);

	if (isPreviewMode() || !checkoutId) return true;
	if (!responded || !themeApplied) return false;
	return !family || fontReady;
};

/**
 * Telas pós-compra (upsell, downsell, obrigado): pronto = conteúdo e checkout
 * resolvidos e página do builder respondida.
 */
export const usePageReady = ({
	contentSettled,
	hasContent,
	checkoutSettled,
	checkoutId,
	extraPending = false
}: {
	contentSettled: boolean;
	hasContent: boolean;
	checkoutSettled: boolean;
	checkoutId?: number;
	/** Algo a mais da tela ainda carregando (ex.: preço do upsell). */
	extraPending?: boolean;
}): boolean => {
	const pageSettled = useCheckoutPageSettled(checkoutId);
	const timedOut = useTimedOut(MAX_SKELETON_MS);

	// No builder o rascunho chega por postMessage: nada a esperar.
	if (isPreviewMode() || timedOut) return true;
	if (!contentSettled) return false;
	if (!hasContent) return true;
	if (!checkoutSettled) return false;
	if (!pageSettled) return false;
	return !extraPending;
};

/** Checkout (avulso/plano): segura o skeleton até o tema da página chegar. */
export const useCheckoutThemeReady = (
	checkoutId: number | undefined,
	isCheckoutResolving: boolean,
	contentUuid?: string
): boolean => {
	const pageSettled = useCheckoutPageSettled(checkoutId, contentUuid);
	const timedOut = useTimedOut(MAX_SKELETON_MS);
	if (isPreviewMode() || timedOut) return true;
	if (isCheckoutResolving) return false;
	return pageSettled;
};
